// Client demographics for the practice dashboard: gender, age, pre-existing
// conditions and DSM-5 diagnosis, counted across every client in scope.
//
// Kept as pure functions with no Supabase import so the counting rules can be
// tested directly. The rules are the whole point of this file — a demographics
// chart is read as a fact about the practice, so the ways it can quietly lie
// are what the tests below pin:
//
//   * A client with no recorded gender is counted as "Not specified", never
//     dropped. Dropping them shrinks the denominator and every other slice
//     silently grows.
//   * Age comes from birth_date at render time, never from patients.age --
//     that column is written once when the client is created and is stale for
//     anyone who has had a birthday since.
//   * preExistingConditions and diagnosis are multi-select, so one client can
//     land in several buckets and the percentages can sum past 100%. That is
//     correct, and the view says so rather than hiding it.

// The dashboard's existing palette, lifted out of DashboardData so the themes
// donut and these charts cannot drift apart.
export const PREMIUM_PALETTE = [
  '#10B981', // Emerald
  '#3B82F6', // Blue
  '#8B5CF6', // Violet
  '#F59E0B', // Amber
  '#EC4899', // Pink
  '#06B6D4', // Cyan
  '#F97316', // Orange
  '#14B8A6', // Teal
  '#A855F7', // Purple
  '#6366F1', // Indigo
  '#D946EF', // Fuchsia
  '#EF4444', // Red
  '#84CC16', // Lime
  '#0EA5E9', // Sky
  '#F43F5E', // Rose
  '#22C55E', // Green
];

export const colorAt = (idx) =>
  idx < PREMIUM_PALETTE.length
    ? PREMIUM_PALETTE[idx]
    : `hsl(${(idx * 137.5) % 360}, 65%, 55%)`; // golden angle: unique hues forever

// Shown instead of omitting a client. Distinct strings because they mean
// different things: nobody filled the field in, versus the clinician recorded
// that there is nothing to record.
export const NOT_SPECIFIED = 'Not specified';
export const NONE_RECORDED = 'None recorded';
export const OTHER = 'Other';

// Under-18 is split out because it changes what a therapy practice may legally
// and clinically do; the rest are the decade bands used by most case-mix
// reporting. Upper bound is exclusive.
export const AGE_BUCKETS = [
  { name: 'Under 18', min: 0, max: 18 },
  { name: '18-24', min: 18, max: 25 },
  { name: '25-34', min: 25, max: 35 },
  { name: '35-44', min: 35, max: 45 },
  { name: '45-54', min: 45, max: 55 },
  { name: '55-64', min: 55, max: 65 },
  { name: '65+', min: 65, max: Infinity },
];

// Postgres text[] usually arrives as a real array, but rows written through
// other paths can carry the stringified form. Same shape as the parser in
// VitalsDashboard, which reads the same two columns.
export function toArray(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value !== 'string') return [];
  const trimmed = value.trim();
  if (!trimmed) return [];
  if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
    try {
      const parsed = JSON.parse(trimmed.replace(/'/g, '"'));
      return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
    } catch {
      return trimmed
        .slice(1, -1)
        .split(',')
        .map((item) => item.trim().replace(/^['"]|['"]$/g, ''))
        .filter(Boolean);
    }
  }
  return [trimmed];
}

/**
 * Whole years elapsed, or null when birth_date is missing, unparseable or in
 * the future. Null is a bucket ("Not specified"), not a zero -- a client whose
 * date of birth never got entered is not a newborn.
 */
export function ageFromBirthDate(birthDate, now = new Date()) {
  if (!birthDate) return null;
  const dob = birthDate instanceof Date ? birthDate : new Date(birthDate);
  if (Number.isNaN(dob.getTime())) return null;
  if (dob > now) return null;

  let age = now.getFullYear() - dob.getFullYear();
  // Their birthday has not come round yet this year, so they are still a year
  // younger. Comparing month-then-day rather than subtracting milliseconds
  // keeps leap years and DST out of it.
  const monthDiff = now.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) age -= 1;
  return age >= 0 ? age : null;
}

export function ageBucketName(age) {
  if (age === null || age === undefined) return NOT_SPECIFIED;
  const bucket = AGE_BUCKETS.find((b) => age >= b.min && age < b.max);
  return bucket ? bucket.name : NOT_SPECIFIED;
}

// Counts -> rows, percentages taken against the number of CLIENTS. For the
// multi-select fields that means "x% of clients carry this tag", which is the
// question a case-mix chart is actually asked, and it is why the column can
// sum past 100%.
function toRows(counts, total, { order, topN } = {}) {
  let entries = Object.entries(counts).filter(([, count]) => count > 0);

  if (order) {
    entries.sort(([a], [b]) => order.indexOf(a) - order.indexOf(b));
  } else {
    // Count desc, then name, so equal counts do not reshuffle between renders.
    entries.sort(([aName, a], [bName, b]) => b - a || aName.localeCompare(bName));
  }

  if (topN && entries.length > topN) {
    const head = entries.slice(0, topN);
    const tail = entries.slice(topN);
    // One bar cannot hold a number this bar chart never shows, so name the
    // bucket with how many distinct values it swallowed.
    const tailTotal = tail.reduce((sum, [, count]) => sum + count, 0);
    entries = [...head, [`${OTHER} (${tail.length})`, tailTotal]];
  }

  return entries.map(([name, count], idx) => ({
    name,
    count,
    percent: total > 0 ? Math.round((count / total) * 100) : 0,
    color: colorAt(idx),
  }));
}

function countSingle(clients, pick, fallback = NOT_SPECIFIED) {
  const counts = {};
  clients.forEach((client) => {
    const raw = pick(client);
    const key = typeof raw === 'string' && raw.trim() ? raw.trim() : fallback;
    counts[key] = (counts[key] || 0) + 1;
  });
  return counts;
}

function countMulti(clients, pick) {
  const counts = {};
  clients.forEach((client) => {
    const values = toArray(pick(client));
    if (values.length === 0) {
      counts[NONE_RECORDED] = (counts[NONE_RECORDED] || 0) + 1;
      return;
    }
    // A client listed twice under the same tag still counts once.
    new Set(values.map((v) => String(v).trim()).filter(Boolean)).forEach((value) => {
      counts[value] = (counts[value] || 0) + 1;
    });
  });
  return counts;
}

/**
 * @param clients decrypted client rows: { gender, birth_date, preExistingConditions, diagnosis }
 * @returns { total, gender, age, conditions, diagnosis } — each an array of
 *          { name, count, percent, color }; `total` is the denominator every
 *          percentage is taken against.
 */
export function aggregateDemographics(clients, { now = new Date(), topN = 8 } = {}) {
  const rows = Array.isArray(clients) ? clients : [];
  const total = rows.length;

  const ageCounts = countSingle(
    rows,
    (c) => ageBucketName(ageFromBirthDate(c?.birth_date, now))
  );

  return {
    total,
    gender: toRows(countSingle(rows, (c) => c?.gender), total),
    // Ordered by life stage, not by size: an age chart sorted by count stops
    // being an age chart.
    age: toRows(ageCounts, total, {
      order: [...AGE_BUCKETS.map((b) => b.name), NOT_SPECIFIED],
    }),
    conditions: toRows(countMulti(rows, (c) => c?.preExistingConditions), total, { topN }),
    diagnosis: toRows(countMulti(rows, (c) => c?.diagnosis), total, { topN }),
  };
}

export default aggregateDemographics;
